import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Row, Col, CardHeader, CardBody, Spinner, Alert } from "reactstrap"
import { getUpgradePreview } from "../../api/requests/subscription-requests"
import BackButton from "../../components/Common/BackButton";

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"
const GREEN = "#71C30B"

const UpgradePreview = () => {
  const { planId } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [preview, setPreview] = useState(null)

  const styles = `
    .upgrade-preview-modern .option-card {
      border: 2px solid #e9ecef;
      border-radius: 1rem;
      padding: 1.5rem;
      transition: all .25s ease;
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    .upgrade-preview-modern .option-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 .75rem 1.5rem rgba(0,0,0,.1);
    }
    .upgrade-preview-modern .option-card.recommended {
      border-color: ${NAVY};
      box-shadow: 0 .5rem 1.25rem rgba(34,28,93,.15);
    }
    .upgrade-preview-modern .summary-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: .45rem 0; font-size: .9rem;
    }
    .upgrade-preview-modern .summary-divider {
      border-top: 1px dashed #dee2e6; margin: .35rem 0;
    }
    .upgrade-preview-modern .total-box {
      border-radius: .75rem; padding: .75rem; text-align: center;
    }
    .upgrade-preview-modern .btn-rounded { border-radius: 999px; }
    .upgrade-preview-modern .btn-navy {
      background: ${NAVY}; border-color: ${NAVY}; color: #fff;
    }
    .upgrade-preview-modern .btn-navy:hover { filter: brightness(1.08); }
    .upgrade-preview-modern .plan-flow {
      display: flex; align-items: center; justify-content: center;
      gap: .75rem; flex-wrap: wrap;
    }
    .upgrade-preview-modern .plan-flow-name {
      font-weight: 700; font-size: 1.1rem; color: #333;
    }
    .upgrade-preview-modern .plan-flow-arrow {
      font-size: 1.5rem; color: ${NAVY};
    }
    .upgrade-preview-modern .date-row {
      display: flex; justify-content: space-between;
      font-size: .8rem; color: #6c757d; padding: .2rem 0;
    }
  `

  document.title = "Paket Yükseltme | Tahsilat360"

  const formatPrice = (price) => {
    if (price == null) return "-"
    return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + " ₺"
  }

  const formatDate = (d) => {
    if (!d) return "-"
    return new Date(d).toLocaleDateString("tr-TR")
  }

  const fetchPreview = async () => {
    setLoading(true)
    setError("")
    try {
      const res = await getUpgradePreview(planId, null)
      setPreview(res.data || res)
    } catch (err) {
      setError(err.message || "Yükseltme seçenekleri alınamadı")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPreview()
  }, [planId])

  return (
    <div className="page-content upgrade-preview-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <Row>
          <Col>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                <div className="d-flex align-items-center gap-3">
                  <BackButton to={"/subscription/plans"} />
                  <div>
                    <h5 className="mb-0 fw-bold text-dark">Paket Yükseltme</h5>
                    <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                      Yükseltme seçeneğinizi belirleyin
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardBody className="pt-3">
                {loading ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Yükseltme seçenekleri hesaplanıyor...</h6>
                  </div>
                ) : error ? (
                  <Alert color="danger" className="rounded-4">
                    <i className="bx bx-error-circle me-2" />
                    {error}
                    <button
                      className="btn btn-sm btn-outline-danger ms-2 btn-rounded"
                      onClick={fetchPreview}
                    >
                      <i className="bx bx-refresh me-1" />Tekrar Dene
                    </button>
                  </Alert>
                ) : preview ? (
                  <>
                    {/* Plan flow header */}
                    <div className="text-center mb-4">
                      <div className="plan-flow mb-2">
                        <span className="plan-flow-name">{preview.currentPlanName}</span>
                        <i className="bx bx-right-arrow-alt plan-flow-arrow" />
                        <span className="plan-flow-name" style={{ color: NAVY }}>{preview.targetPlanName}</span>
                      </div>
                      <span
                        className="badge"
                        style={{
                          borderRadius: 999,
                          backgroundColor: preview.remainingDays > 30 ? GREEN : "#E56A22",
                          color: "#fff",
                          fontSize: ".8rem",
                          padding: ".4rem .85rem",
                        }}
                      >
                        <i className="bx bx-time-five me-1" />
                        {preview.remainingDays > 0
                          ? `Mevcut pakette ${preview.remainingDays} gün kaldı`
                          : "Mevcut paket süresi doldu"}
                        {preview.currentEndDate && ` · ${formatDate(preview.currentEndDate)}`}
                      </span>
                    </div>

                    {/* Two option cards */}
                    <Row className="g-4">
                      {/* Card 1 - IMMEDIATE: kalan sürede kullan */}
                      {preview.immediateOption && (
                        <Col md={6}>
                          <div className="option-card recommended">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h5 className="fw-bold mb-0" style={{ color: NAVY }}>
                                <i className="bx bx-zap me-2" />
                                Kalan Sürede Kullan
                              </h5>
                              <span
                                className="badge"
                                style={{
                                  borderRadius: 999,
                                  backgroundColor: GREEN,
                                  color: "#fff",
                                  fontSize: ".7rem",
                                  padding: ".3rem .7rem",
                                }}
                              >
                                Önerilen
                              </span>
                            </div>
                            <p className="text-muted small mb-3">
                              Yeni paket bugün başlar, mevcut paketinizin bitiş tarihine kadar kullanılır.
                              Yalnız kalan günlerin bedelini ödersiniz.
                            </p>

                            <div className="flex-grow-1">
                              <div className="summary-row">
                                <span className="text-muted">Kalan {preview.remainingDays} günün bedeli</span>
                                <span className="fw-medium">
                                  {formatPrice(preview.immediateOption.targetPlanRemainingCost)}
                                </span>
                              </div>
                              <div className="summary-divider" />
                              <div className="total-box mt-2" style={{ backgroundColor: "#EBF0F7" }}>
                                <div className="text-muted small mb-1">Toplam</div>
                                <div className="fw-bold" style={{ fontSize: "1.75rem", color: NAVY, lineHeight: 1.2 }}>
                                  {formatPrice(preview.immediateOption.totalAmount)}
                                </div>
                              </div>

                              <div className="mt-3 border-top pt-2">
                                <div className="date-row">
                                  <span>Başlangıç</span>
                                  <span className="fw-medium text-dark">{formatDate(preview.immediateOption.startDate)}</span>
                                </div>
                                <div className="date-row">
                                  <span>Bitiş</span>
                                  <span className="fw-medium text-dark">{formatDate(preview.immediateOption.endDate)}</span>
                                </div>
                              </div>
                            </div>

                            <button
                              className="btn btn-navy btn-rounded w-100 py-2 mt-3 fw-bold"
                              onClick={() => navigate(`/subscription/checkout/${planId}?upgradeType=IMMEDIATE`)}
                            >
                              <i className="bx bx-zap me-1" />
                              Kalan Sürede Geç
                            </button>
                          </div>
                        </Col>
                      )}

                      {/* Card 2 - EXTENSION: yeni dönem */}
                      {preview.extensionOption && (
                        <Col md={6}>
                          <div className="option-card">
                            <div className="mb-3">
                              <h5 className="fw-bold mb-0" style={{ color: "#555" }}>
                                <i className="bx bx-calendar me-2" />
                                Yeni Dönem Başlat
                              </h5>
                            </div>
                            <p className="text-muted small mb-3">
                              Yeni paket bugün başlar ve tam süresi kadar geçerli olur. Mevcut paketinizin
                              kalan değeri fiyattan düşülür.
                            </p>

                            <div className="flex-grow-1">
                              <div className="summary-row">
                                <span className="text-muted">Paket fiyatı</span>
                                <span className="fw-medium">
                                  {formatPrice(preview.extensionOption.basePrice)}
                                </span>
                              </div>
                              {preview.extensionOption.couponDiscount > 0 && (
                                <div className="summary-row">
                                  <span className="text-muted">Kupon indirimi</span>
                                  <span className="fw-medium" style={{ color: "#2E7D32" }}>
                                    -{formatPrice(preview.extensionOption.couponDiscount)}
                                  </span>
                                </div>
                              )}
                              {preview.extensionOption.currentPlanCredit > 0 && (
                                <div className="summary-row">
                                  <span className="text-muted">Mevcut paketin kalan değeri</span>
                                  <span className="fw-medium" style={{ color: "#2E7D32" }}>
                                    -{formatPrice(preview.extensionOption.currentPlanCredit)}
                                  </span>
                                </div>
                              )}
                              <div className="summary-row">
                                <span className="text-muted">Ara toplam</span>
                                <span className="fw-medium">
                                  {formatPrice(preview.extensionOption.subtotal)}
                                </span>
                              </div>
                              <div className="summary-divider" />
                              <div className="total-box mt-2" style={{ backgroundColor: "#f5f5f5" }}>
                                <div className="text-muted small mb-1">Toplam</div>
                                <div className="fw-bold" style={{ fontSize: "1.75rem", color: "#333", lineHeight: 1.2 }}>
                                  {formatPrice(preview.extensionOption.totalAmount)}
                                </div>
                              </div>

                              <div className="mt-3 border-top pt-2">
                                <div className="date-row">
                                  <span>Başlangıç</span>
                                  <span className="fw-medium text-dark">{formatDate(preview.extensionOption.startDate)}</span>
                                </div>
                                <div className="date-row">
                                  <span>Bitiş</span>
                                  <span className="fw-medium text-dark">{formatDate(preview.extensionOption.endDate)}</span>
                                </div>
                              </div>
                            </div>

                            <button
                              className="btn btn-outline-secondary btn-rounded w-100 py-2 mt-3 fw-bold"
                              onClick={() => navigate(`/subscription/checkout/${planId}?upgradeType=EXTENSION`)}
                            >
                              <i className="bx bx-calendar me-1" />
                              Yeni Dönem Başlat
                            </button>
                          </div>
                        </Col>
                      )}
                      {preview.immediateOption && !preview.extensionOption && (
                        <Col xs={12}>
                          <div className="text-muted small">
                            <i className="bx bx-info-circle me-1" />
                            Sonraki döneminiz önceden ödendiği için yalnız kalan süre seçeneğiyle yükseltme yapılabilir.
                          </div>
                        </Col>
                      )}
                    </Row>
                  </>
                ) : null}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default UpgradePreview
